/**
 * Browser router — single deployment, three shells.
 *
 * The Vite bundle picks one of three route trees at boot based on
 * ``resolveShell()`` (hostname classifier from PR 2):
 *   - ``platform``       → marketing-only ``PlatformMarketingShell``
 *   - ``platform-admin`` → cross-tenant ``PlatformAdminShell``
 *   - ``tenant``         → buyer flow + org admin ``TenantServiceShell``
 *
 * All routes mount under ``/:lang/*``; the ``App`` element (in
 * ``apps/web/src/App.tsx``) handles the language guard + chrome.
 * The ``pickChildrenFor`` export is consumed by
 * ``tests/shell/routerShellSelection.test.tsx`` to assert each
 * shell's tree directly without mocking ``window.location``.
 */

import {
  createBrowserRouter,
  Navigate,
  Outlet,
  useLocation,
  useParams,
  type RouteObject,
} from "react-router-dom";

import App from "@/App";
import { AccentProvider } from "@/components/AccentProvider";
import { ThemeProvider } from "@/components/ThemeProvider";
import { withRouteErrorBoundary } from "@/components/error/withRouteErrorBoundary";
import EmbedPage from "@/embed/EmbedPage";
import AuthContinue from "@/pages/AuthContinue";
import AuthHop from "@/pages/AuthHop";
import { DEFAULT_LANG } from "@/i18n";
import { getRuntimeDefaultLang } from "@/i18n/languageRegistry";
import { platformAdminChildren } from "@/shell/PlatformAdminShell";
import { platformChildren } from "@/shell/PlatformMarketingShell";
import { resolveShell, type ShellKind } from "@/shell/resolveShell";
import { tenantChildren } from "@/shell/TenantServiceShell";

export function pickChildrenFor(shell: ShellKind): RouteObject[] {
  switch (shell) {
    case "platform":
      return platformChildren;
    case "platform-admin":
      return platformAdminChildren;
    default:
      return tenantChildren;
  }
}

const activeChildren = pickChildrenFor(resolveShell());

/**
 * Traceability F5 — label deep-link shortener. Printed labels encode
 * ``{origin}/t/{uid}`` (language-neutral, short = smaller QR matrix);
 * this route expands it to the localized admin trace page. Single
 * source of the encoded shape is ``trace_url`` on the BE.
 */
function TraceShortlinkRedirect(): JSX.Element {
  const { uid } = useParams<{ uid: string }>();
  return <Navigate to={`/${DEFAULT_LANG}/admin/trace/${uid ?? ""}`} replace />;
}

/** Redirect the bare ``/`` (and unmatched paths) to the runtime default
 * locale. Reads the registry so an admin-changed default language takes
 * effect at the entry URL (falls back to ``cs`` before branding warms). */
function RootLangRedirect(): JSX.Element {
  // The query survives the hop: an outreach link is `/?p=<code>` (KAN-1574).
  const { search } = useLocation();
  return <Navigate to={{ pathname: `/${getRuntimeDefaultLang()}`, search }} replace />;
}

/**
 * KAN-1243 — the published DPA (art. 7.2, annex 3) points readers at the
 * subprocessor list by the bare path ``/subprocessors`` and declares that
 * list part of the contract. Without this route the catch-all below sent
 * them to the landing page. Unlike ``RootLangRedirect`` the path is kept:
 * it lands on the localized list, not on the home page.
 */
function SubprocessorsRedirect(): JSX.Element {
  return <Navigate to={`/${getRuntimeDefaultLang()}/legal/subprocessors`} replace />;
}

/**
 * Theme + accent for everything except the two hop interstitials.
 *
 * Both providers call ``useAuthMe``. Mounted above ``RouterProvider`` they
 * probed the session on ``/auth/continue`` too — a host where the visitor
 * is by definition not signed in yet — so every hop fired ``GET /auth/me``,
 * got 401, and ``apiFetch`` answered with ``POST /auth/refresh`` in the
 * same instant the redeem was setting the new cookie. When the rotation
 * won, it revoked the session the redeem had just minted (KAN-1207).
 * Under a layout route the probe simply does not exist on those two paths.
 */
function ShellProviders(): JSX.Element {
  return (
    <ThemeProvider>
      <AccentProvider>
        <Outlet />
      </AccentProvider>
    </ThemeProvider>
  );
}

export const routes: RouteObject[] = [
  // Cross-host session transfer landing strip. Deliberately OUTSIDE
  // ``/:lang``: the visitor has no session on this host yet, so the
  // language gate (which reads tenant branding) would bounce them
  // before the token is redeemed — and the token dies in 60 seconds.
  // It carries its own ``next`` and hands over to the real route.
  { path: "/auth/continue", element: <AuthContinue /> },
  // The other end of the same hop: mints the token on the host that
  // holds the session, then forwards to ``/auth/continue`` over there.
  // Outside ``/:lang`` for the same reason — it carries its own
  // ``next`` and must not spend the 60-second TTL on a language
  // redirect. Mounted on ALL THREE shells: the hop starts wherever the
  // user is signed in, which for these links is the marketing site or
  // the staff console as often as a tenant.
  { path: "/auth/hop", element: <AuthHop /> },
  {
    element: <ShellProviders />,
    children: [
      { path: "/", element: <RootLangRedirect /> },
      { path: "/t/:uid", element: <TraceShortlinkRedirect /> },
      { path: "/subprocessors", element: <SubprocessorsRedirect /> },
      // Embeddable configurator. Deliberately OUTSIDE ``/:lang``: it has
      // no header, footer or nav, and its language arrives as ``?lang=``
      // from the snippet the tenant pasted. The security headers for this
      // path are per-tenant and set by ``apps/web/middleware.ts``.
      // Since KAN-223 production serves ``/embed`` from its own entry
      // (``embed.html``); this route stays as the safety net for any host
      // where that rewrite does not apply — slower, but it still renders.
      { path: "/embed", element: <EmbedPage /> },
      {
        path: "/:lang",
        element: <App />,
        children: withRouteErrorBoundary(activeChildren),
      },
      { path: "*", element: <RootLangRedirect /> },
    ],
  },
];

export const router = createBrowserRouter(routes);
